import { useReducer } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import {
  buscarActualizacion,
  reducirComprobacion,
  versionLegible,
} from '#components/mobile/actualizacion/actualizacion';
import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { registrarUso } from '#uso';

type ActualizacionesProps = {
  /** Inyectable para los tests; por defecto, el service worker real. */
  buscar?: () => Promise<boolean>;
  aplicar?: () => Promise<void> | void;
  /** Línea «Versión de la app» (Rumbo ya la enseña aparte en Ajustes). */
  conVersion?: boolean;
};

/** Ajustes › Versión: versión legible y «Buscar actualizaciones». */
export function Actualizaciones({
  buscar = buscarActualizacion,
  aplicar = () => global.Actual.applyAppUpdate(),
  conVersion = true,
}: ActualizacionesProps) {
  const { t } = useTranslation();
  const [estado, despachar] = useReducer(reducirComprobacion, 'inactivo');

  // «Versión del cliente» ya sale encima; aquí se añade el commit si la
  // build lo conoce (REACT_APP_COMMIT), p. ej. «v26.9.0 · a1b2c3d».
  const commit = import.meta.env.REACT_APP_COMMIT;
  const version =
    conVersion && commit
      ? versionLegible(window.Actual?.ACTUAL_VERSION, commit)
      : null;

  async function onBuscar() {
    despachar({ tipo: 'buscar' });
    try {
      const hayNueva = await buscar();
      despachar({ tipo: 'resultado', hayNueva });
      if (hayNueva) {
        registrarUso('actualizacion_mostrada', { origen: 'ajustes' });
      }
    } catch {
      despachar({ tipo: 'fallo' });
    }
  }

  function onActualizar() {
    despachar({ tipo: 'aplicar' });
    registrarUso('actualizacion_aplicada', { origen: 'ajustes' });
    void aplicar();
  }

  const mensaje =
    estado === 'buscando'
      ? t('Checking…')
      : estado === 'al-dia'
        ? t('You already have the latest version')
        : estado === 'hay-nueva'
          ? t('There is a new version')
          : estado === 'error'
            ? t('Could not check. Try again when you are online.')
            : estado === 'aplicando'
              ? t('Updating…')
              : null;

  return (
    <View style={{ gap: 10 }} data-testid="ajustes-actualizaciones">
      {version && (
        <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
          {t('App version: {{version}}', { version })}
        </Text>
      )}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Boton
          variante="fantasma"
          style={{ border: `1px solid ${color.line2}` }}
          onPress={() => void onBuscar()}
          isDisabled={estado === 'buscando' || estado === 'aplicando'}
        >
          <Trans>Check for updates</Trans>
        </Boton>
        {(estado === 'hay-nueva' || estado === 'aplicando') && (
          <Boton onPress={onActualizar} isDisabled={estado === 'aplicando'}>
            <Trans>Update</Trans>
          </Boton>
        )}
      </View>
      {mensaje && (
        <Text
          role="status"
          style={{
            ...texto.cuerpo,
            fontWeight: 700,
            color:
              estado === 'al-dia'
                ? color.ok
                : estado === 'error'
                  ? color.bad
                  : color.fg,
          }}
        >
          {mensaje}
        </Text>
      )}
    </View>
  );
}
